import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const SITE_URL = "https://www.eco-bain.ca";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const routeSeo = {
  "/": {
    canonical: "/",
    indexable: true,
    fr: {
      title: "Réémaillage de baignoire à Montréal | Éco-Bain",
      description:
        "Réémaillage de baignoire à Montréal dès 385 $. Sans démolition, intervention en 90 minutes, baignoire utilisable dès le lendemain. Garantie jusqu’à 5 ans.",
    },
    en: {
      title: "Bathtub reglazing in Montreal | Éco-Bain",
      description:
        "Bathtub reglazing in Montreal from $385. No demolition, 90-minute on-site service, bathtub usable the next day. Warranty up to 5 years.",
    },
  },
  "/service-de-reemaillage": {
    canonical: "/service-de-reemaillage",
    indexable: true,
    serviceSchema: true,
    fr: {
      title: "Service de réémaillage de baignoire à Montréal | Éco-Bain",
      description:
        "Réémaillage en 90 minutes de baignoires, lavabos, éviers, douches et céramique, avec réparation acrylique à Montréal et environs.",
    },
    en: {
      title: "Bathtub and surface reglazing in Montreal | Éco-Bain",
      description:
        "90-minute reglazing for bathtubs, sinks, showers and wall tile, plus acrylic-surface repair in Montreal and surrounding areas.",
    },
  },
  "/liste-de-prix": {
    canonical: "/liste-de-prix",
    indexable: false,
    fr: {
      title: "Liste de prix | Éco-Bain",
      description: "Consultez les tarifs Éco-Bain pour le réémaillage et la réparation de surfaces de salle de bain.",
    },
    en: {
      title: "Price list | Éco-Bain",
      description: "View Éco-Bain pricing for reglazing and bathroom-surface repair services.",
    },
  },
  "/a-savoir": {
    canonical: "/a-savoir",
    indexable: false,
    fr: {
      title: "À savoir avant notre intervention | Éco-Bain",
      description: "Informations importantes à connaître avant l’intervention d’Éco-Bain.",
    },
    en: {
      title: "Important information | Éco-Bain",
      description: "Important information to review before Éco-Bain's intervention.",
    },
  },
  "/avis-aux-utilisateurs": {
    canonical: "/avis-aux-utilisateurs",
    indexable: false,
    fr: {
      title: "Avis aux utilisateurs | Éco-Bain",
      description: "Consignes d’utilisation et d’entretien après une restauration Éco-Bain.",
    },
    en: {
      title: "User notice | Éco-Bain",
      description: "Usage and maintenance instructions after an Éco-Bain restoration.",
    },
  },
  "/ai-preview": {
    canonical: "/ai-preview",
    indexable: false,
    fr: {
      title: "Aperçu IA Éco-Bain | Simulation de réémaillage",
      description: "Visualisez une simulation de réémaillage avant de demander votre devis Éco-Bain.",
    },
    en: {
      title: "Éco-Bain AI Preview | Reglazing simulation",
      description: "Preview a reglazing simulation before requesting an Éco-Bain estimate.",
    },
  },
};

function setMeta(selector, attributes, content) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(href) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", href);
}

export default function RouteSeo() {
  const { pathname } = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const seo = routeSeo[pathname] || {
      canonical: pathname,
      indexable: false,
      fr: { title: "Page introuvable | Éco-Bain", description: "Cette page n’est pas disponible." },
      en: { title: "Page not found | Éco-Bain", description: "This page is unavailable." },
    };
    const localized = seo[language] || seo.fr;
    const canonicalUrl = `${SITE_URL}${seo.canonical === "/" ? "/" : seo.canonical}`;

    document.documentElement.lang = language === "en" ? "en" : "fr";
    document.title = localized.title;
    setCanonical(canonicalUrl);
    setMeta('meta[name="description"]', { name: "description" }, localized.description);
    setMeta(
      'meta[name="robots"]',
      { name: "robots" },
      seo.indexable ? "index, follow, max-image-preview:large" : "noindex, follow"
    );
    setMeta('meta[property="og:title"]', { property: "og:title" }, localized.title);
    setMeta('meta[property="og:description"]', { property: "og:description" }, localized.description);
    setMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    setMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    setMeta('meta[property="og:image"]', { property: "og:image" }, OG_IMAGE);
    setMeta('meta[property="og:locale"]', { property: "og:locale" }, language === "en" ? "en_CA" : "fr_CA");

    const existingServiceSchema = document.getElementById("route-service-schema");
    if (!seo.serviceSchema) {
      existingServiceSchema?.remove();
      return;
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_URL}/service-de-reemaillage#service`,
      name: language === "en" ? "Bathtub and bathroom-surface reglazing" : "Réémaillage de baignoire et surfaces de salle de bain",
      url: `${SITE_URL}/service-de-reemaillage`,
      provider: { "@id": `${SITE_URL}/#business` },
      areaServed: { "@type": "City", name: "Montréal" },
      serviceType:
        language === "en"
          ? "Bathtub and bathroom-surface reglazing and repair"
          : "Réémaillage et réparation de baignoires et surfaces de salle de bain",
      description: localized.description,
    };
    const script = existingServiceSchema || document.createElement("script");
    script.id = "route-service-schema";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    if (!existingServiceSchema) document.head.appendChild(script);
  }, [language, pathname]);

  return null;
}
